import React, { useState } from "react";
import { Drawer, Button, CircularProgress, Tooltip, FormControlLabel, Checkbox } from "@mui/material";
import { ShoppingCartOutlined, CloseOutlined, Timer } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

import PurchaseSummary from "./components/PurchaseSummary";
import PaymentMethodSelector from "./components/PaymentMethodSelector";
import CampoCodigoCanal from "../../../../../../components/canales/CampoCodigoCanal";
import QRPaymentModal from "./components/QRPaymentModal";
import SecondDrawer from "../second-drawer/SecondDrawer";

import { usePaymentMethods } from "../../../../../../hooks/usePaymentMethods";
import { useCooldown } from "../../../../../../hooks/useCooldown";
import { usePaymentProcessing } from "../../../../../../hooks/usePaymentProcessing";
import { useTermsAndConditions } from "../../../../../../hooks/useTermsAndConditions";
import { usePolling } from "../../../../../../hooks/usePolling";

import {
  calcularSubtotal,
  calcularComision,
  calculoSubtotalTarifa,
  formatCurrency,
  todasTarifasGratuitas,
  todasTarifasConAprobacion,
  hayTarifasConAprobacion,
  hayTarifasPagas
} from "../../../../../../utils/paymentCalculations";

const FirstDrawerDesktop = ({
  data,
  cantidad,
  setCantidad,
  toggleDrawer,
  openDrawer,
  tarifasSeleccionadas,
  asientosSeleccionados,
  setAsientosSeleccionados,
}) => {
  const param = useParams();
  const [codigoCanal, setCodigoCanal] = useState("");
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [openSecondDrawer, setOpenSecondDrawer] = useState(false);
  const [dataTarifas, setDataTarifas] = useState([]);

  // hook para los manejar que metodo de pago mostrar
  const {
    medioPago,
    setMedioPago,
    multiplesMetodosPago,
    mostrarBotonQR,
    mostrarBotonLink,
  } = usePaymentMethods(data?.medios_pago, param.id);

  //cooldown para la compra
  const {
    cooldownActive,
    cooldownRemaining,
    startCooldown,
    formatRemainingTime
  } = useCooldown();

  // hook para manejar el uso de terminos y condiciones 
  const {
    tieneLink,
    checkboxChecked,
    handleCheckboxChange
  } = useTermsAndConditions(data?.descripcion);

  // hook para manejar el polling si se genera un qr 
  const {
    startPolling,
    stopPolling,
    resetPolling,
    paymentStatus: pollingPaymentStatus
  } = usePolling();

  // manejo de pagos, generacion de qr, carrito, etc
  const {
    loadingCarrito,
    qrModalOpen,
    qrData,
    handlePagoDirecto,
    handleCloseQrModal,
    formatQRImage,
    qrExpirationTime,
  } = usePaymentProcessing({
    data,
    cantidad,
    tarifasSeleccionadas,
    asientosSeleccionados,
    medioPago,
    param,
    setAsientosSeleccionados,
    setCantidad,
    setMostrarFormulario,
    setOpenSecondDrawer,
    startCooldown,
    startPolling,
    stopPolling,
    resetPolling,
    codigoCanal
  });

  const subtotal = calcularSubtotal(data?.tarifas, cantidad, asientosSeleccionados);
  const comision = calcularComision(data?.tarifas, cantidad, asientosSeleccionados);
  const total = subtotal + comision;
  const hayComision = data?.tarifas?.some((tarifa) => {
    const cantidadTarifa = cantidad[tarifa.id] || 0;
    return cantidadTarifa > 0 && !tarifa?.asumir_comision;
  });

  const checkTarifasPreguntas = () => {
    return tarifasSeleccionadas.some(tarifa => {
      if (tarifa.informacion_adicional === false) return false;
      return tarifa.adicionales?.some(adicional => {
        return adicional.informacion_adicional.length > 0;
      });
    });
  };

  const handleButtonClick = () => {
    if (loadingCarrito) return;
    if (checkTarifasPreguntas()) {
      setOpenSecondDrawer(true);
    } else {
      handlePagoDirecto();
    }
  };

  const handleCloseDrawer = () => {
    const hasSelectedTarifas = Object.values(cantidad).some(value => value > 0);
    if (hasSelectedTarifas) {
      const userConfirmed = window.confirm(
        "¿Estás seguro de que quieres cerrar? Se perderán las tarifas seleccionadas."
      );
      if (!userConfirmed) return;
    }

    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
    setCantidad({});
    toggleDrawer(false)();
  };

  return (
    <>
      <Drawer
        sx={{
          minWidth: "80%",
          maxHeight: "85vh",
          padding: "40px",
          "& .MuiDrawer-paper": { borderRadius: "10px 10px 0 0", minHeight: "80%", maxHeight: "85vh" }
        }}
        transitionDuration={500}
        anchor="bottom"
        open={openDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleCloseDrawer();
          }
        }}
      >
        <div style={{ padding: "40px" }}>
          <div className="d-flex flex-row justify-content-between">
            <p className="fw-bold mb-3" style={{ color: "#221A60", fontSize: "20px" }}>
              Resumen de tu compra
            </p>
            <button type="button" onClick={handleCloseDrawer} style={{ paddingRight: "16px" }}>
              <CloseOutlined sx={{ width: "16px", height: "17px" }} />
            </button>
          </div>

          <div className="d-flex flex-row w-100">
            <PurchaseSummary
              tarifasSeleccionadas={tarifasSeleccionadas}
              cantidad={cantidad}
              calculoSubtotalTarifa={(tarifa) => calculoSubtotalTarifa(tarifa, cantidad, asientosSeleccionados)}
              subtotal={subtotal}
              comision={comision}
              hayComision={hayComision}
              total={total}
              formatCurrency={formatCurrency}
            />

            <div style={{ flex: 1, paddingRight: "8px" }}>
              <CampoCodigoCanal eventoId={param.id} value={codigoCanal} onChange={setCodigoCanal} />
              {(!todasTarifasGratuitas(tarifasSeleccionadas, cantidad) || hayTarifasConAprobacion(tarifasSeleccionadas, cantidad)) && (
                <PaymentMethodSelector
                  medioPago={medioPago}
                  setMedioPago={setMedioPago}
                  mediosPago={data?.medios_pago}
                  eventoId={param.id}
                  mostrarBotonLink={mostrarBotonLink}
                  mostrarBotonQR={mostrarBotonQR}
                  todasGratuitas={todasTarifasGratuitas(tarifasSeleccionadas, cantidad)}
                  todasConAprobacion={todasTarifasConAprobacion(tarifasSeleccionadas, cantidad)}
                  hayAprobacion={hayTarifasConAprobacion(tarifasSeleccionadas, cantidad)}
                />
              )}

              {tieneLink && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxChecked}
                      onChange={handleCheckboxChange}
                      required
                      size="small"
                    />
                  }
                  label={<small>Confirmo haber leído y acepto los términos y condiciones del organizador del evento</small>}
                />
              )}

              <Tooltip
                title={cooldownActive ? `Debes esperar antes de crear otro link de pago (${formatRemainingTime(cooldownRemaining)})` : ""}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    className="boton-submit w-100"
                    onClick={handleButtonClick}
                    disabled={
                      (tieneLink && !checkboxChecked) ||
                      (multiplesMetodosPago && !medioPago && hayTarifasPagas(tarifasSeleccionadas, cantidad)) ||
                      loadingCarrito ||
                      cooldownActive
                    }
                  >
                    {loadingCarrito ? (
                      <>
                        <CircularProgress size="18px" sx={{ color: "white", marginRight: "10px" }} />
                        <span>Procesando...</span>
                      </>
                    ) : cooldownActive ? (
                      <>
                        <Timer sx={{ marginRight: "5px" }} />
                        <span>Espera {formatRemainingTime(cooldownRemaining)}</span>
                      </>
                    ) : (
                      <span>
                        <ShoppingCartOutlined sx={{ color: "white", width: "18px", height: "18px", marginRight: "5px" }} />
                        {todasTarifasConAprobacion(tarifasSeleccionadas, cantidad)
                          ? (checkTarifasPreguntas() ? "Continuar" : "Solicitar acreditación")
                          : todasTarifasGratuitas(tarifasSeleccionadas, cantidad)
                            ? "Obtener tickets"
                            : (checkTarifasPreguntas() ? "Continuar con la compra" : "Finalizar compra")
                        }
                      </span>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </Drawer>

      <SecondDrawer
        onBack={() => setOpenSecondDrawer(false)}
        handlePagoDirecto={handlePagoDirecto}
        dataTarifas={dataTarifas}
        setDataTarifas={setDataTarifas}
        loadingCarrito={loadingCarrito}
        cantidad={cantidad}
        open={openSecondDrawer}
        onClose={handleCloseDrawer}
        setOpenSecondDrawer={setOpenSecondDrawer}
        tarifasSeleccionadas={tarifasSeleccionadas}
      />

      <QRPaymentModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        qrData={qrData}
        paymentStatus={pollingPaymentStatus}
        qrExpirationTime={qrExpirationTime}
        paymentReference={qrData?.reference || qrData?.referencia}
        formatRemainingTime={formatRemainingTime}
        formatQRImage={formatQRImage}
      />
    </>
  );
};

export default FirstDrawerDesktop;